import { useApolloClient } from '@vue/apollo-composable';

import {
  GetMessengerMessageDocument,
  type GetMessengerMessageQuery,
  MessengerMessageFragmentDoc,
} from '@shared/api/graphql/gql/graphql';

import type { MessageStatusEvent, MessengerMessage } from './types';

/**
 * Резолвер сообщения по id для тонкого WS-события.
 *
 * Тонкое событие несёт лишь `{id, conversation_id}` — тело берём из
 * нормализованного Apollo-кэша (`readFragment` по id), а при промахе добираем
 * одним точечным запросом `messengerMessage(id)` (пишет в кэш). `patchStatus`
 * обновляет поля статуса нормализованной сущности прямо в кэше — без добора.
 */
export const useMessengerMessageResolver = () => {
  const { resolveClient } = useApolloClient();

  const identify = (id: string): string | undefined =>
    resolveClient().cache.identify({ __typename: 'MessengerMessage', id });

  const readFromCache = (id: string): MessengerMessage | null => {
    const cacheId = identify(id);
    if (!cacheId) {
      return null;
    }

    return (
      resolveClient().cache.readFragment({
        id: cacheId,
        fragment: MessengerMessageFragmentDoc,
        fragmentName: 'MessengerMessage',
      }) ?? null
    );
  };

  const fetchById = async (id: string): Promise<MessengerMessage | null> => {
    const response: { data?: GetMessengerMessageQuery | null } = await resolveClient().query({
      query: GetMessengerMessageDocument,
      variables: { id },
      // Добор — свежие данные; заодно нормализуется в кэш по id.
      fetchPolicy: 'network-only',
    });

    return response.data?.messengerMessage ?? null;
  };

  /** Достать сообщение: из кэша, при промахе — добор одним запросом. */
  const resolve = async (id: string): Promise<MessengerMessage | null> => {
    return readFromCache(id) ?? (await fetchById(id));
  };

  /**
   * Пропатчить поля статуса сообщения в кэше из тонкого статус-события.
   *
   * @returns true — сообщение было в кэше (пропатчено); false — нужно добрать.
   */
  const patchStatus = (event: MessageStatusEvent): boolean => {
    const cacheId = identify(event.id);
    if (!cacheId || !readFromCache(event.id)) {
      return false;
    }

    resolveClient().cache.modify({
      id: cacheId,
      fields: {
        status: () => event.status,
        sent_at: () => event.sent_at,
        delivered_at: () => event.delivered_at,
        read_at: () => event.read_at,
        failed_reason: () => event.failed_reason,
      },
    });

    return true;
  };

  return { readFromCache, resolve, patchStatus };
};
